import { useEffect, useState, useCallback, useLayoutEffect, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import ReactDOM from "react-dom"

import CheckboxCell from "../../../common/interactive/CheckboxCell"
import Button from "../../../common/Button"
import { practicesOptions, retrieveTransportaionsData } from "../../../../lib/systemData"
import { deleteTransportationRide } from "../../../../lib/fetchApi"
import { notificationActions } from "../../../../store/notifications"
import { caseActions } from "../../../../store/case"
import { formatCurrency, formatDateLocal } from "../../../../lib/script";
import { shouldShowAddressOverrideAlert } from "../../../../lib/rideAddressMatch"
import { useTooltipAutoClose, useTooltipPortalFade } from "../../../../lib/useTooltipAutoClose"

import classes from './RideItem.module.css'
import tooltipClasses from "../../../common/GlobalTooltip.module.css"

/** e.g. droppedOff -> Dropped Off, ride_requested -> Ride Requested */
function formatRideStatusLabel(status) {
    if (status == null || status === '') {
        return status
    }
    const str = String(status).trim()
    let withSpaces = str.replace(/_/g, ' ')
    withSpaces = withSpaces.replace(/([a-z])([A-Z])/g, '$1 $2')
    return withSpaces
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
}

function formatMileage(value) {
    if (value === null || value === undefined || value === '') {
        return ''
    }
    const num = Number(value)
    if (Number.isNaN(num)) {
        return value
    }
    return num.toFixed(2)
}

function AddressOverrideReasonIcon({ reason }) {
    const triggerRef = useRef(null)
    const tooltipElRef = useRef(null)
    const { isOpen, isVisible, isClosing, open, close } = useTooltipPortalFade()
    const [coordinates, setCoordinates] = useState({ top: null, left: null })

    const updateCoordinates = useCallback(() => {
        if (triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect()
            setCoordinates({
                top: rect.top,
                left: rect.left + rect.width / 2,
            })
        }
    }, [])

    useLayoutEffect(() => {
        if (isOpen) {
            updateCoordinates()
        }
    }, [isOpen, updateCoordinates])

    useTooltipAutoClose({
        isOpen,
        onClose: close,
        triggerRef,
        tooltipRef: tooltipElRef,
        interactive: false,
    })

    const overlayRoot = document.getElementById("overlay-root")

    return (
        <span
            className={classes.reason_icon_trigger}
            ref={triggerRef}
            onPointerEnter={() => {
                open()
                updateCoordinates()
            }}
            onPointerLeave={close}
            aria-label="Address override reason"
        >
            <i className={`ti ti-info-circle ${classes.reason_icon}`} />
            {isVisible && overlayRoot && ReactDOM.createPortal(
                <div
                    ref={tooltipElRef}
                    className={`${tooltipClasses.tooltip_modal} ${isClosing ? tooltipClasses.closing : ""}`}
                    style={{ top: coordinates.top, left: coordinates.left }}
                >
                    {reason}
                </div>,
                overlayRoot,
            )}
        </span>
    )
}

const RideItem = ({item, isMinorPatient = false}) => {
    const dispatch = useDispatch()

    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)

    const [loadCancel, setLoadCancel] = useState(false)
    const [clinicName, setClinicName] = useState(null)

    const cancelRideHandler = async () => {
        setLoadCancel(true)
        try {
            let deleteRide = await deleteTransportationRide(userToken, activeCase.case.id, item.id, dispatch)

            if (deleteRide && deleteRide.errorMessage) {
                dispatch(notificationActions.setError(deleteRide))
            } else {
                dispatch(notificationActions.setError({errorMessage: 'success', type: 'success'}))
                
                let dispatchBody = {
                    folder: 'case_transportation',
                    label: 'rides',
                    item: deleteRide
                }

                dispatch(caseActions.changetemInObjArray(dispatchBody))
            }
        } finally {
            setLoadCancel(false)
        }
    }

    useEffect(() => {
        let practices = practicesOptions()
        if (practices)  {
            practices.forEach(elem => {                
                if (elem.data.locations) {
                    elem.data.locations.forEach(element => {
                        if (element.id == item.location_id) {
                            setClinicName(element.code)
                            {/* display_name */}
                        }
                    })
                    
                }
            })
        }
    }, [item])

    let fromName = item.pickup_at == 'home' ? item.address : item.pickup_at == 'other' ? item.addresses?.from : clinicName
    let toName = item.pickup_at == 'home' ? clinicName : item.pickup_at == 'other' ? item.addresses?.to : item.address

    const patientSideAddress = item.pickup_at == 'home' || item.pickup_at == 'clinic' ? item.address : null
    const showAddressOverrideReason = shouldShowAddressOverrideAlert({
        reason: item.address_override_reason,
        rideAddress: patientSideAddress,
        patientContact: activeCase?.patient?.contact_information,
    })

    const renderAddressCell = (value, isPatientSide) => (
        <span className={classes.address_cell}>
            {isPatientSide && showAddressOverrideReason && (
                <AddressOverrideReasonIcon reason={item.address_override_reason} />
            )}
            <span className={classes.address_text}>{value}</span>
        </span>
    )

    const transportationSettings = retrieveTransportaionsData()
    const lyftRideDetailsBaseUrl = transportationSettings?.lyft_ride_details_url
    const lyftRideDetailsUrl =
        lyftRideDetailsBaseUrl && item?.lyft_ride_id
            ? `${String(lyftRideDetailsBaseUrl).trim().replace(/\/+$/, '')}/${String(item.lyft_ride_id).trim()}`
            : null

    return <>
        <tr key={item.id} className={item.active == 0 ? classes.item_deleted : ''}>
            <td>
                {lyftRideDetailsUrl ? (
                    <a href={lyftRideDetailsUrl} target="_blank" rel="noreferrer">
                        {item.lyft_ride_id}
                    </a>
                ) : (
                    item.lyft_ride_id
                )}
            </td>
            <td>{formatRideStatusLabel(item.lyft_ride_status)}</td>
            <td>{formatDateLocal(item.date)}</td>
            <td>{renderAddressCell(fromName, item.pickup_at == 'home')}</td>
            <td>{renderAddressCell(toName, item.pickup_at == 'clinic')}</td>
            <td>
                {item.route_url && <a href={item.route_url} target="_blank" rel="noreferrer">View</a>}
            </td>
            <td>{formatMileage(item.total_milage)}</td>
            <td>${formatCurrency(item.trip_charge)}{item.trip_charge != item.case_trip_charge ? ` / $${formatCurrency(item.case_trip_charge)}` : ''}</td>
            <td>${formatCurrency(item.lyft_charge)}{item.lyft_charge != item.case_lyft_charge ? ` / $${formatCurrency(item.case_lyft_charge)}` : ''}</td>
            <td><CheckboxCell checked={item.is_shared} isDisabled={true}/></td>
            {isMinorPatient && <td>{item.guardian_name || ''}</td>}
            {isMinorPatient && <td>{item.guardian_relationship || ''}</td>}
            <td>
                <div className={classes.controls_container}>
                    {item.active == 1 && item.completed !=1 && <Button click={cancelRideHandler} isLoading={loadCancel} loaderType={'dark'}>Cancel</Button>}
                </div>
            </td>
        </tr>
    </>
}

export default RideItem